import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Mic, HeartPulse, Landmark, Home as HomeIcon, Briefcase, GraduationCap,
  Car, Users, HeartHandshake, Wallet, Sparkles, ArrowRight, Check, X, PhoneOff,
  Volume2, ShieldCheck, Chrome,
} from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import WhoGuidesDemo from "@/components/WhoGuidesDemo";

const AGENTS = [
  { key: "healthcare",  label: "Healthcare",         Icon: HeartPulse,     angle: 270, desc: "Coordinates appointments, referrals, providers, records, and follow-up." },
  { key: "benefits",    label: "Benefits",           Icon: Landmark,       angle: 305, desc: "Helps navigate VA, state, and federal benefits while keeping organizations informed." },
  { key: "housing",     label: "Housing",            Icon: HomeIcon,       angle: 340, desc: "Coordinates housing partners, applications, and stability supports." },
  { key: "employment",  label: "Employment",         Icon: Briefcase,      angle: 15,  desc: "Connects workforce partners, credentialing, and career progression." },
  { key: "education",   label: "Education",          Icon: GraduationCap,  angle: 50,  desc: "Coordinates GI Bill, credentialing, and post-secondary transitions." },
  { key: "transport",   label: "Transportation",     Icon: Car,            angle: 85,  desc: "Coordinates rides, mobility services, and travel to critical appointments." },
  { key: "family",      label: "Family",             Icon: Users,          angle: 165, desc: "Coordinates family programs, caregiver resources, and school transitions." },
  { key: "community",   label: "Community",          Icon: HeartHandshake, angle: 205, desc: "Connects peer support, faith communities, and local nonprofits." },
  { key: "financial",   label: "Financial Wellness", Icon: Wallet,         angle: 235, desc: "Coordinates financial counseling, benefits maximization, and stability supports." },
];

const RING_R = 220; // radius of node ring in SVG coords (viewBox 640x640, center 320,320)

const pos = (angle) => {
  const rad = (angle * Math.PI) / 180;
  return { x: 320 + RING_R * Math.cos(rad), y: 320 + RING_R * Math.sin(rad) };
};

export default function Experience() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="experience-page" className="bg-navy-deep text-cream">
      <PilotDemoBanner tone="navy" />
      <Seo
        title="How WHO Works | The Signature Experience of WHO Guides™"
        description="Step inside the WHO Guides™ intelligence coordination network. One conversation. Thousands of coordinated actions. Try a live conversation with a WHO Guide in your browser — no sign-up."
        path="/experience"
      />

      <HeroAgentNetwork />
      <InteractiveAgents />
      <LiveDemo />
      <TwoTimelines />
      <ClosingHero />
    </div>
  );
}

/* ============================================================
   SECTION — Try It Live (real Web Speech + Claude + OpenAI TTS)
============================================================ */
function LiveDemo() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-24 lg:py-32 bg-cream text-navy border-y border-borderSubtle"
      data-testid="experience-live-demo"
      id="try-it-live"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-gold/12 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/8 blur-3xl" />
      </div>

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="eyebrow text-gold-deep"
            data-testid="live-demo-eyebrow"
          >
            Try It Live &middot; No sign-up
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-[3.4rem]"
          >
            Speak to Maya.<br />
            <span className="text-gold-deep">Hear a WHO Guide respond.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-6 text-lg text-slateSoft leading-relaxed max-w-lg"
          >
            This is not a video. It&apos;s the real WHO Guides&trade; system, live in your browser.
            Tap the microphone, say something a person navigating a real transition might say &mdash;
            and a WHO Guide (voice-native, agentic AI) will respond in real time.
          </motion.p>

          <ul className="mt-8 space-y-3.5" data-testid="live-demo-how-to">
            {[
              { icon: <Mic className="w-4 h-4" />,          t: "Tap the mic",            b: "Your browser will ask permission to access the microphone." },
              { icon: <Volume2 className="w-4 h-4" />,      t: "Speak naturally",         b: 'Try: "WHO, I just lost my job." or "WHO, I need help finding housing."' },
              { icon: <Sparkles className="w-4 h-4" />,     t: "Hear the Guide respond",  b: "Maya (a WHO Guide) replies in real time — powered by Claude + OpenAI TTS." },
              { icon: <ShieldCheck className="w-4 h-4" />,  t: "Nothing is stored",       b: "The demo does not save your audio. Transcripts are used only to generate the response." },
            ].map((step, i) => (
              <motion.li
                key={step.t}
                initial={{ opacity: 0, x: -8 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="flex items-start gap-3.5"
              >
                <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-gold/15 border border-gold/40 text-gold-deep">
                  {step.icon}
                </span>
                <div>
                  <p className="font-display font-bold text-navy leading-tight">{step.t}</p>
                  <p className="mt-0.5 text-sm text-slateSoft leading-relaxed">{step.b}</p>
                </div>
              </motion.li>
            ))}
          </ul>

          <div className="mt-8 p-4 bg-white border border-borderSubtle rounded-md flex items-start gap-3" data-testid="live-demo-browser-note">
            <Chrome className="w-4 h-4 text-slateSoft mt-0.5 shrink-0" />
            <p className="text-xs text-slateSoft leading-relaxed">
              <span className="font-semibold text-navy">Browser support:</span> Chrome, Edge, or Safari on
              desktop and iOS work best. Firefox does not currently support browser-native voice
              recognition. If the toggle isn&apos;t visible, refresh in a supported browser.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative">
            {/* Attention pointer badge */}
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-gold text-navy text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full shadow-lg"
              data-testid="live-demo-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-navy animate-pulse" />
              Live &middot; Real AI &middot; Real audio
            </motion.div>
            <WhoGuidesDemo variant="teaser" defaultMode="live" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SECTION 1 — Meet WHO Guides™ (Prompt 1)
============================================================ */
function HeroAgentNetwork() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-28 lg:pt-36 pb-24 lg:pb-32 border-b border-white/5"
      data-testid="experience-hero"
    >
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full bg-gold/12 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[720px] h-[720px] rounded-full bg-gold/8 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-[0.08]" />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="eyebrow text-gold"
            data-testid="exp-eyebrow"
          >
            Meet WHO Guides&trade;
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mt-4 font-display font-black text-cream tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[3.6rem]"
          >
            One Conversation.<br />
            <span className="text-gold">Thousands of Coordinated Actions.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-6 text-lg text-cream/80 leading-relaxed max-w-lg"
          >
            WHO Guides&trade; combines human expertise with intelligent AI agents that coordinate
            services, reduce administrative burden, and help ensure every transition is
            <span className="text-cream font-semibold"> completed &mdash; not merely started</span>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/platform"
              data-testid="exp-hero-see-how-btn"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              See How WHO Works <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-cream/30 hover:bg-cream/10 text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              Join the Movement
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <AgentNetworkSvg inView={inView} withLabels />
        </div>
      </div>
    </section>
  );
}

/* Shared SVG network — used in Section 1 and Section 2 */
function AgentNetworkSvg({ inView, withLabels = false, activeAgent = null, onHover = null }) {
  return (
    <div className="relative w-full aspect-square max-w-[640px] mx-auto">
      <svg viewBox="0 0 640 640" className="w-full h-full">
        <defs>
          <radialGradient id="core-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#DDB27C" stopOpacity="0.75" />
            <stop offset="0.5" stopColor="#C6934C" stopOpacity="0.35" />
            <stop offset="1" stopColor="#C6934C" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="line-grad" x1="0" x2="1">
            <stop offset="0" stopColor="#DDB27C" stopOpacity="0.9" />
            <stop offset="1" stopColor="#C6934C" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        {/* Ambient rings — rotate the group instead of the circle to avoid SVG attribute type warnings */}
        {[280, 240, 200].map((r, i) => (
          <motion.g
            key={r}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 0.15 - i * 0.03, rotate: 360 } : {}}
            transition={{ opacity: { duration: 1.2, delay: 0.4 + i * 0.15 }, rotate: { duration: 60 + i * 20, repeat: Infinity, ease: "linear" } }}
            style={{ transformOrigin: "320px 320px" }}
          >
            <circle cx="320" cy="320" r={r} fill="none" stroke="#DDB27C" strokeWidth="0.5" strokeDasharray="2 6" />
          </motion.g>
        ))}

        {/* Core glow */}
        <motion.circle
          cx="320" cy="320" r="140"
          fill="url(#core-glow)"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: [0.55, 0.85, 0.55] } : {}}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Connector lines from core to each agent */}
        {AGENTS.map((a, i) => {
          const p = pos(a.angle);
          const isActive = activeAgent === a.key;
          return (
            <motion.line
              key={`line-${a.key}`}
              x1="320" y1="320" x2={p.x} y2={p.y}
              stroke={isActive ? "#DDB27C" : "url(#line-grad)"}
              strokeWidth={isActive ? 2 : 1.2}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={inView ? { pathLength: 1, opacity: isActive ? 1 : 0.55 } : {}}
              transition={{ delay: 0.6 + i * 0.08, duration: 0.9, ease: "easeInOut" }}
            />
          );
        })}

        {/* Pulsing signal orbs traveling from core to each node */}
        {AGENTS.map((a, i) => {
          const p = pos(a.angle);
          return (
            <motion.circle
              key={`pulse-${a.key}`}
              r="3"
              fill="#DDB27C"
              initial={{ cx: 320, cy: 320, opacity: 0 }}
              animate={inView ? { cx: [320, p.x], cy: [320, p.y], opacity: [0, 1, 0] } : {}}
              transition={{ duration: 2.4, delay: 1.8 + i * 0.35, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
            />
          );
        })}

        {/* Agent nodes */}
        {AGENTS.map((a, i) => {
          const p = pos(a.angle);
          const isActive = activeAgent === a.key;
          return (
            <motion.g
              key={a.key}
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 1 + i * 0.08, type: "spring", stiffness: 220, damping: 18 }}
              onMouseEnter={onHover ? () => onHover(a.key) : undefined}
              onMouseLeave={onHover ? () => onHover(null) : undefined}
              style={{ cursor: onHover ? "pointer" : "default" }}
            >
              <circle
                cx={p.x} cy={p.y} r={isActive ? 30 : 26}
                fill="#0F2138"
                stroke={isActive ? "#DDB27C" : "#C6934C"}
                strokeWidth={isActive ? 2.5 : 1.5}
              />
              {isActive && (
                <circle cx={p.x} cy={p.y} r={38} fill="none" stroke="#DDB27C" strokeWidth="1" opacity="0.6" />
              )}
              <foreignObject x={p.x - 12} y={p.y - 12} width="24" height="24">
                <div style={{ color: isActive ? "#DDB27C" : "#C6934C", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <a.Icon size={20} strokeWidth={2} />
                </div>
              </foreignObject>
            </motion.g>
          );
        })}

        {/* Center — WHO Guides */}
        <motion.g
          initial={{ opacity: 0, scale: 0.6 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
        >
          <circle cx="320" cy="320" r="60" fill="#0F2138" stroke="#DDB27C" strokeWidth="2" />
          <motion.circle
            cx="320" cy="320" r="60" fill="none" stroke="#DDB27C" strokeWidth="1"
            animate={{ r: [60, 88, 60], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeOut" }}
          />
          <foreignObject x="296" y="296" width="48" height="48">
            <div style={{ color: "#DDB27C", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Mic size={28} strokeWidth={2.2} />
            </div>
          </foreignObject>
        </motion.g>

        {/* Node labels (only in hero variant) */}
        {withLabels && AGENTS.map((a, i) => {
          const p = pos(a.angle);
          const isLeft = p.x < 320;
          const tx = isLeft ? p.x - 34 : p.x + 34;
          return (
            <motion.text
              key={`lbl-${a.key}`}
              x={tx} y={p.y + 4}
              fill="#F6F1E6"
              fontSize="11"
              fontWeight="600"
              textAnchor={isLeft ? "end" : "start"}
              letterSpacing="0.5"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 0.75 } : {}}
              transition={{ delay: 1.4 + i * 0.06, duration: 0.4 }}
            >
              {a.label}
            </motion.text>
          );
        })}
      </svg>

      {/* Center label under SVG */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="mt-24 lg:mt-32 text-center">
          <p className="text-[10px] uppercase tracking-[0.24em] text-gold font-bold">WHO Guides&trade;</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SECTION 2 — Interactive Agent Network (Prompt 3)
============================================================ */
function InteractiveAgents() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [active, setActive] = useState(null);
  const activeAgent = AGENTS.find((a) => a.key === active);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-24 lg:py-32 bg-navy border-y border-white/5"
      data-testid="experience-interactive"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 dot-grid opacity-[0.06]" />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="eyebrow text-gold">The intelligence layer</p>
          <h2 className="mt-4 font-display font-black text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
            We don&apos;t just answer questions.<br />
            <span className="text-gold">We coordinate journeys.</span>
          </h2>
          <p className="mt-6 text-cream/75 leading-relaxed text-lg">
            Hover any node to meet a specialist Guide. Each is a focused AI agent, always
            working in concert with a real WHO Guide and your existing organizations.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <AgentNetworkSvg inView={inView} activeAgent={active} onHover={setActive} />
          </div>

          <div className="lg:col-span-5">
            <motion.div
              key={active || "default"}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="p-8 rounded-2xl bg-white/[0.04] border border-white/10 min-h-[280px]"
              data-testid="experience-agent-panel"
            >
              {activeAgent ? (
                <>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center">
                      <activeAgent.Icon className="w-6 h-6 text-gold" strokeWidth={2.2} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-bold">Specialist</p>
                      <h3 className="mt-1 font-display font-bold text-cream text-2xl leading-tight">
                        {activeAgent.label} Guide&trade;
                      </h3>
                    </div>
                  </div>
                  <p className="mt-6 text-cream/80 leading-relaxed">
                    {activeAgent.desc}
                  </p>
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-2 text-[11px] uppercase tracking-widest text-gold-light">
                    <Sparkles className="w-3.5 h-3.5" />
                    Human-in-the-loop · Consent-first · Tracked to outcome
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-gold" />
                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-bold">
                      Hover a node to explore
                    </p>
                  </div>
                  <p className="mt-5 font-display font-bold text-cream text-2xl leading-tight">
                    Nine specialist Guides,<br />coordinated by one intelligence.
                  </p>
                  <p className="mt-4 text-cream/70 leading-relaxed">
                    Each specialist works alongside real WHO Guides and your partner organizations
                    &mdash; understanding needs, coordinating services, tracking referrals,
                    confirming completion, and measuring outcomes.
                  </p>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SECTION 3 — Two Timelines (Prompt 4)
============================================================ */
const T1 = [
  { t: "Person receives five referrals" },
  { t: "No follow-up" },
  { t: "No accountability" },
  { t: "Journey ends" },
];

const T2 = [
  { t: "Person meets WHO Guides™" },
  { t: "WHO activates specialist Guides" },
  { t: "Healthcare · Housing · Employment · Benefits · Education" },
  { t: "Every connection tracked" },
  { t: "Successful transition" },
];

function TwoTimelines() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-24 lg:py-32 bg-cream text-navy border-y border-borderSubtle"
      data-testid="experience-timelines"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <p className="eyebrow text-gold-deep">AI that helps get things done</p>
          <h2 className="mt-4 font-display font-black tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl text-navy">
            Two systems.<br />
            <span className="text-gold-deep">One measurable difference.</span>
          </h2>
          <p className="mt-6 text-slateSoft leading-relaxed text-lg max-w-2xl">
            Most platforms hand a person a list of numbers and hope for the best. WHO Guides&trade;
            stay in the loop &mdash; through every organization, every appointment, every follow-up
            &mdash; until the transition is real.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-stretch">
          {/* Timeline 1 — Broken */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="p-8 lg:p-10 bg-cream-panel border border-borderSubtle rounded-2xl"
            data-testid="timeline-broken"
          >
            <div className="flex items-center gap-3">
              <PhoneOff className="w-5 h-5 text-slateSoft" />
              <p className="eyebrow text-slateSoft">Traditional Referral</p>
            </div>
            <h3 className="mt-3 font-display font-black text-navy text-2xl leading-tight">The journey ends where the referral begins.</h3>
            <ol className="mt-8 relative border-l-2 border-dashed border-slateSoft/40 pl-6 space-y-6">
              {T1.map((s, i) => (
                <motion.li
                  key={s.t}
                  initial={{ opacity: 0, x: -8 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.4 }}
                  className="relative"
                >
                  <span className="absolute -left-[33px] top-0.5 w-5 h-5 rounded-full bg-navy text-cream flex items-center justify-center">
                    <X className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <p className="text-navy leading-relaxed">{s.t}</p>
                </motion.li>
              ))}
            </ol>
            <div className="mt-8 pt-6 border-t border-borderSubtle">
              <p className="text-slateSoft font-display italic">
                Endpoint: <span className="text-navy font-semibold">Unknown</span>
              </p>
            </div>
          </motion.div>

          {/* Timeline 2 — WHO */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="p-8 lg:p-10 bg-navy-deep text-cream rounded-2xl relative overflow-hidden"
            data-testid="timeline-who"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10" aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-gold" />
                <p className="eyebrow text-gold-light">With WHO Guides&trade;</p>
              </div>
              <h3 className="mt-3 font-display font-black text-cream text-2xl leading-tight">Every connection stays tracked until the transition completes.</h3>
              <ol className="mt-8 relative border-l-2 border-gold/60 pl-6 space-y-6">
                {T2.map((s, i) => (
                  <motion.li
                    key={s.t}
                    initial={{ opacity: 0, x: -8 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.35 + i * 0.15, duration: 0.4 }}
                    className="relative"
                  >
                    <span className="absolute -left-[33px] top-0.5 w-5 h-5 rounded-full bg-gold text-navy flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3.5} />
                    </span>
                    <p className="text-cream leading-relaxed">{s.t}</p>
                  </motion.li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gold text-navy flex items-center justify-center">
                  <Sparkles className="w-5 h-5" strokeWidth={2.4} />
                </div>
                <p className="text-cream font-display font-bold text-lg">
                  Successful Transition
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SECTION 4 — Closing (Prompt 10)
============================================================ */
function ClosingHero() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-32 lg:py-40 bg-navy-deep text-cream"
      data-testid="experience-closing"
    >
      {/* Ambient node field background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <svg viewBox="0 0 1200 600" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          {Array.from({ length: 60 }).map((_, i) => {
            const x = (i * 137) % 1200;
            const y = (i * 271) % 600;
            return (
              <motion.circle
                key={i}
                cx={x} cy={y} r={1.5}
                fill="#DDB27C"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: [0, 0.6, 0] } : {}}
                transition={{ duration: 3 + (i % 3), repeat: Infinity, delay: (i % 12) * 0.3, ease: "easeInOut" }}
              />
            );
          })}
          {/* connecting web */}
          {Array.from({ length: 30 }).map((_, i) => {
            const x1 = (i * 89) % 1200;
            const y1 = (i * 173) % 600;
            const x2 = ((i + 7) * 89) % 1200;
            const y2 = ((i + 7) * 173) % 600;
            return (
              <motion.line
                key={`w-${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#C6934C" strokeWidth="0.4"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: [0, 0.15, 0] } : {}}
                transition={{ duration: 5 + (i % 4), repeat: Infinity, delay: (i % 10) * 0.5, ease: "easeInOut" }}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/30 via-transparent to-navy-deep" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <motion.p
          initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="eyebrow text-gold"
        >
          The future of care coordination
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="mt-6 font-display font-black text-cream text-5xl sm:text-6xl lg:text-[4.5rem] leading-[1.02] tracking-tight"
        >
          The future isn&apos;t<br />
          Artificial Intelligence.<br />
          <span className="text-gold">It&apos;s Accountable Intelligence.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-10 text-cream/80 text-lg leading-relaxed"
        >
          Every Connection Matters.<br />
          Every Journey Counts.<br />
          Every Person Deserves Better.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-12 flex flex-wrap justify-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="experience-closing-join-btn"
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-8 py-4 rounded-md transition-colors text-lg"
          >
            Join the Movement <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </Link>
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 border border-cream/30 hover:bg-cream/10 text-cream font-semibold px-8 py-4 rounded-md transition-colors text-lg"
          >
            Become a Partner
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
